import React, { useEffect, type FC, Fragment } from 'react';
import { styled } from '@compiled/react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import PremiumIcon from '@atlaskit/icon/glyph/premium';
import { token } from '@atlaskit/tokens';

import { ConfluenceEdition } from '@confluence/change-edition';
import { GradientCTAInvertedButton } from '@confluence/change-edition/entry-points/GradientCTAButton';
import { Attribution, withTransparentErrorBoundary } from '@confluence/error-boundary';
import { useCanUpgradeAndPayForProductUser } from '@confluence/experiment-more-product-users-can-upgrade-pay';
import { useAutoUpgradeAwareness } from '@confluence/experiment-auto-upgrade-awareness';

import { useUpflowModal } from '../useUpflowModal';
import { constructUIEvent } from '../constructPersistentUpgradeAnalyticsEvents';
import { PERSISTENT_UPGRADE_FLOWID, PERSISTENT_UPGRADE_TOUCHPOINT_BUTTON } from '../constants';

import { usePersistentUpgradeButton } from './usePersistentUpgradeButton';

const TEXT_MIN_WIDTH = '59px';
const TEXT_MAX_WIDTH = '155px';

const i18n = defineMessages({
	upgrade: {
		id: 'persistent-upgrade.button.upgrade',
		defaultMessage: 'Upgrade',
		description: 'Upgrade message',
	},
	youreOnTheFreePlan: {
		id: 'persistent-upgrade.free.plan.heading',
		defaultMessage: 'You‘re on the Free plan',
		description: 'Informing users they‘re on the free plan',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UpgradeButtonWrapper = styled.div({
	borderTop: `1px solid ${token('color.border', '#dfe1e6')}`,
	padding: token('space.200', '16px'),
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
});

type PersistentUpgradeButtonComponentProps = {
	isEligibleForMpu?: boolean;
	mpuAnalyticsAttributes?: {
		isBillingAdmin?: boolean;
		isSiteAdmin?: boolean;
		mpuCohort?: string;
	};
};

export const PersistentUpgradeButtonComponent: FC<PersistentUpgradeButtonComponentProps> = ({
	isEligibleForMpu,
	mpuAnalyticsAttributes,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { openUpflowModal } = useUpflowModal(PERSISTENT_UPGRADE_TOUCHPOINT_BUTTON);
	const { auaAnalyticAttributes } = useAutoUpgradeAwareness();

	useEffect(() => {
		createAnalyticsEvent(
			constructUIEvent({
				eventAction: 'viewed',
				touchpointSubject: PERSISTENT_UPGRADE_TOUCHPOINT_BUTTON,
				currentEdition: ConfluenceEdition.FREE,
				touchpointId: PERSISTENT_UPGRADE_TOUCHPOINT_BUTTON,
				flowId: PERSISTENT_UPGRADE_FLOWID,
				experimentAttributes: {
					...(isEligibleForMpu && mpuAnalyticsAttributes),
					...auaAnalyticAttributes,
				},
			}),
		).fire();
	}, [createAnalyticsEvent, mpuAnalyticsAttributes, isEligibleForMpu, auaAnalyticAttributes]);

	return (
		<Fragment>
			<GradientCTAInvertedButton
				iconBefore={<PremiumIcon label="Premium" size="small" />}
				onClick={openUpflowModal}
				textMinWidth={TEXT_MIN_WIDTH}
				textMaxWidth={TEXT_MAX_WIDTH}
			>
				<FormattedMessage {...i18n.upgrade} />
			</GradientCTAInvertedButton>
		</Fragment>
	);
};

type PersistentUpgradeButtonProps = {
	isInAppNavigation?: boolean;
};

export const PersistentUpgradeButton: FC<PersistentUpgradeButtonProps> =
	withTransparentErrorBoundary({
		attribution: Attribution.CC_ONBOARDING,
	})(({ isInAppNavigation = false }) => {
		const { isPersistentUpgradeButtonEligible } = usePersistentUpgradeButton();

		const {
			analyticsAttributes: mpuAnalyticsAttributes,
			isEligibleForMpu,
			loading: mpuLoading,
		} = useCanUpgradeAndPayForProductUser();

		if (mpuLoading) {
			return null;
		}

		if (isPersistentUpgradeButtonEligible && isInAppNavigation) {
			return (
				<UpgradeButtonWrapper>
					<PersistentUpgradeButtonComponent
						mpuAnalyticsAttributes={mpuAnalyticsAttributes}
						isEligibleForMpu={isEligibleForMpu}
					/>
				</UpgradeButtonWrapper>
			);
		}

		return null;
	});
